import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

/**
 * RegisterPage component for Agent Nexus
 *
 * General registration page that allows users to create an account.
 * This is a standalone page not tied to the CDI Trial workflow.
 *
 * Features:
 * - Email and password registration with form validation
 * - Organization name input
 * - Terms of Service and PHI acknowledgment checkboxes
 * - Password visibility toggle
 * - Disabled SSO buttons (Google and Microsoft)
 * - Backend integration with registration API
 * - Error message display
 * - Loading states
 * - Responsive design
 */
const RegisterPage = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    organization_name: '',
    terms_accepted: false,
    phi_acknowledgment: false
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState('');
  const [emailExists, setEmailExists] = useState(false);

  /**
   * Handle input changes for form fields
   * Updates form state and clears errors
   *
   * @param {Object} e - The input change event
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (error) setError('');
    if (emailExists) setEmailExists(false);
  };

  /**
   * Validate form data before submission
   * Checks all required fields and validation rules
   *
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.organization_name.trim()) {
      setError('Organization name is required');
      return false;
    }
    if (!formData.terms_accepted) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return false;
    }
    if (!formData.phi_acknowledgment) {
      setError('You must acknowledge the de-identified data policy');
      return false;
    }
    return true;
  };

  /**
   * Handle form submission
   * Validates form, sends registration request to backend,
   * and navigates to verification page on success
   *
   * @param {Object} e - The form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setRequestId('');
    setEmailExists(false);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      const reqId = data.request_id || response.headers.get('X-Request-ID');

      if (!response.ok) {
        if (response.status === 409) {
          setEmailExists(true);
        } else {
          setError(data.detail || 'Registration failed. Please try again.');
        }
        if (reqId) setRequestId(reqId);
        return;
      }

      // Store token and email for the session
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_email', formData.email);

      // Navigate to email verification page
      navigate('/cdi-trial/step-1-verify', {
        state: {
          email: formData.email,
          message: data.message,
          fromRegistration: true
        }
      });

    } catch (err) {
      setError('Unable to connect to the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          {/* Gradient header inside the form card */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6">
            <h1 className="text-3xl font-bold text-white text-center">Create Your Account</h1>
          </div>

          {/* Form content */}
          <div className="p-8">
            {/* Existing account notice */}
            {emailExists && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  We already have an account with that email address on file. Please click the button below to reset your password.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password', { state: { email: formData.email } })}
                  className="mt-3 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Reset Your Password
                </button>
                {requestId && (
                  <p className="text-xs text-blue-400 dark:text-blue-500 mt-2">
                    Reference ID: <code className="font-mono">{requestId}</code>
                  </p>
                )}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                {requestId && (
                  <p className="text-xs text-red-400 dark:text-red-500 mt-1">
                    Reference ID: <code className="font-mono">{requestId}</code>
                  </p>
                )}
              </div>
            )}

            {/* Registration form */}
            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                  placeholder="your.email@company.com"
                />
              </div>

              {/* Password field with visibility toggle */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength="8"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              {/* Confirm Password field with visibility toggle */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength="8"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 ${
                      formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500">
                    Passwords do not match
                  </p>
                )}
              </div>

              {/* Organization Name field */}
              <div>
                <label htmlFor="organization_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization Name *
                </label>
                <input
                  id="organization_name"
                  name="organization_name"
                  type="text"
                  required
                  value={formData.organization_name}
                  onChange={handleChange}
                  disabled={loading}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                  placeholder="Your Organization"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 pt-2">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleChange}
                    disabled={loading}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    required
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    I accept the <a href="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">Privacy Policy</a>
                  </span>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="phi_acknowledgment"
                    checked={formData.phi_acknowledgment}
                    onChange={handleChange}
                    disabled={loading}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    required
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    I acknowledge that this platform accepts <strong>de-identified data only</strong> (no PHI/PII)
                  </span>
                </label>
              </div>

              {/* Submit button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </form>

            {/* Sign in link */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300">
                  Sign In
                </Link>
              </p>
            </div>
          </div>  {/* closes p-8 */}
        </div>    {/* closes max-w-md form card */}
      </div>      {/* closes flex container */}
    </div>        {/* closes container mx-auto */}
  </section>
);
};

export default RegisterPage;
